import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { ConvexProviderWithClerk } from 'convex/react-clerk';
import { ConvexReactClient } from 'convex/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import App from './App';
import './styles.css';

/**
 * Configuration is checked at BUILD time by scripts/web-env-check.mjs, which is the real
 * defence: Vite inlines these values, so a missing one cannot be fixed after the fact.
 *
 * This runtime check is the backstop, and it deliberately does NOT throw at module scope.
 * A top-level throw on a statically-undefined value lets the minifier discard the entire
 * application as unreachable — producing a bundle that contains nothing but the throw,
 * from a build that exited 0.
 */
const convexUrl = import.meta.env.VITE_CONVEX_URL;
const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

function Misconfigured({ missing }: { missing: string[] }) {
  return (
    <main style={{ fontFamily: 'system-ui', padding: '3rem', maxWidth: 640 }}>
      <h1>TagVault console is misconfigured</h1>
      <p>This build is missing {missing.join(' and ')}. It was built without them, so it cannot connect.</p>
      <p style={{ color: '#5d7079' }}>Set them in the Netlify site environment and redeploy.</p>
    </main>
  );
}

function Root() {
  const missing = [
    convexUrl ? null : 'VITE_CONVEX_URL',
    clerkKey ? null : 'VITE_CLERK_PUBLISHABLE_KEY',
  ].filter((x): x is string => x !== null);

  if (missing.length > 0) return <Misconfigured missing={missing} />;

  const convex = new ConvexReactClient(convexUrl);
  return (
    <ClerkProvider publishableKey={clerkKey}>
      <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
        <BrowserRouter>
          <Routes>
            <Route path="/*" element={<App />} />
          </Routes>
        </BrowserRouter>
      </ConvexProviderWithClerk>
    </ClerkProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
